// Frontend/src/components/leave/LeaveRequestStatus.jsx
import React, { useState, useEffect } from 'react';
import { useLeave } from '../../context/LeaveContext';
import useEmployeeProfile from '../../hooks/useEmployeeProfile';
import { leaveService } from '../../api/services';
import { Calendar, Clock, CheckCircle2, XCircle, AlertCircle, RefreshCw, Filter, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';

const LeaveRequestStatus = ({ hideCard = false }) => {
  const { myLeaveRequests, fetchMyLeaveRequests, loading } = useLeave();
  const employeeProfile = useEmployeeProfile();
  const [statusFilter, setStatusFilter] = useState('All');
  const [refreshing, setRefreshing] = useState(false);
  const [cancellingRequestId, setCancellingRequestId] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ open: false, request: null });

  useEffect(() => {
    if (!employeeProfile.isLoading && employeeProfile.employeeNo) {
      loadRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeProfile.isLoading, employeeProfile.employeeNo, statusFilter]);

  const loadRequests = async () => {
    if (!employeeProfile.employeeNo) {
      console.log('⏳ Waiting for employee number...');
      return;
    }
    await fetchMyLeaveRequests(employeeProfile.employeeNo);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadRequests();
    setRefreshing(false);
  };

  const handleCancelRequest = (request) => {
    setConfirmModal({ open: true, request });
  };

  const handleConfirmCancel = async () => {
    const request = confirmModal.request;
    setConfirmModal({ open: false, request: null });

    const requestId = request.RequestId || request.id;
    const dataAreaId = request.dataAreaId || 'usmf';

    setCancellingRequestId(requestId);

    try {
      console.log('🗑️ Cancelling leave request:', requestId, 'dataAreaId:', dataAreaId);
      await leaveService.cancelLeaveRequest(requestId, dataAreaId);
      console.log('✅ Leave request cancelled successfully');
      toast.success('Leave request cancelled successfully!');
      await loadRequests();
    } catch (error) {
      console.error('❌ Failed to cancel leave request:', error);
      toast.error(`Failed to cancel leave request: ${error.message}`);
    } finally {
      setCancellingRequestId(null);
    }
  };

  // Check if request can be cancelled (only Pending/Submitted/Draft)
  const canCancelRequest = (leaveStatus) => {
    return ['Pending', 'Submitted', 'Draft'].includes(leaveStatus);
  };

  // Filter requests on client side since API doesn't support status filtering
  const filteredRequests = statusFilter === 'All'
    ? myLeaveRequests
    : myLeaveRequests.filter(req => (req.leaveStatus || req.status) === statusFilter);

  // Show loading state for employee profile
  const isInitialLoading = employeeProfile.isLoading || (loading && myLeaveRequests.length === 0);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Approved':
      case 'Completed':
        return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case 'Rejected':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'Pending':
      case 'Submitted':
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      case 'Cancelled':
      case 'Canceled':
        return <XCircle className="w-5 h-5 text-gray-600" />;
      case 'Draft':
        return <Clock className="w-5 h-5 text-blue-600" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      Pending: 'bg-yellow-100 text-yellow-800',
      Submitted: 'bg-yellow-100 text-yellow-800',
      Approved: 'bg-green-100 text-green-800',
      Completed: 'bg-green-100 text-green-800',
      Rejected: 'bg-red-100 text-red-800',
      Cancelled: 'bg-gray-100 text-gray-800',
      Canceled: 'bg-gray-100 text-gray-800',
      Draft: 'bg-blue-100 text-blue-800',
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const content = (
    <>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Calendar className="w-6 h-6 text-indigo-600" />
          My Leave Requests
        </h2>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Filter */}
        <div className="mb-6 flex items-center gap-4">
          <Filter className="w-5 h-5 text-gray-500" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="All">All Statuses</option>
            <option value="Draft">Draft</option>
            <option value="Pending">Pending</option>
            <option value="Submitted">Submitted</option>
            <option value="Approved">Approved</option>
            <option value="Completed">Completed</option>
            <option value="Rejected">Rejected</option>
            <option value="Cancelled">Cancelled</option>
          </select>
          <span className="text-sm text-gray-600">
            Showing {filteredRequests.length} request{filteredRequests.length !== 1 ? 's' : ''}
          </span>
        </div>

        {isInitialLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            <p className="ml-3 text-gray-600">Loading leave requests...</p>
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No leave requests found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredRequests.map((request) => (
              <div
                key={request.id}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getStatusIcon(request.leaveStatus || request.status)}
                      <h3 className="text-lg font-semibold text-gray-800">
                        {request.leaveType} - {request.leaveTypeName || 'Leave Request'}
                      </h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusBadge(request.leaveStatus || request.status)}`}>
                        {request.leaveStatus || request.status}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="w-4 h-4" />
                        <span>
                          {formatDate(request.startDate)} - {formatDate(request.endDate)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Clock className="w-4 h-4" />
                        <span>
                          {request.leaveAmountUnit === 'Hours'
                            ? `${request.totalHours || 0} hour${request.totalHours !== 1 ? 's' : ''}`
                            : `${request.totalHours || 0} day${request.totalHours !== 1 ? 's' : ''}`}
                        </span>
                      </div>
                      {request.approverName && (
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">Approved by:</span> {request.approverName}
                        </div>
                      )}
                    </div>
                    {request.reason && (
                      <p className="mt-2 text-sm text-gray-600">
                        <span className="font-medium">Reason:</span> {request.reason}
                      </p>
                    )}
                    {request.rejectionReason && (
                      <p className="mt-2 text-sm text-red-600">
                        <span className="font-medium">Rejection Reason:</span> {request.rejectionReason}
                      </p>
                    )}
                    <div className="mt-3 flex gap-4 text-xs text-gray-500">
                      <span>Requested: {formatDate(request.requestedDate)}</span>
                      {request.approvedDate && (
                        <span>Approved: {formatDate(request.approvedDate)}</span>
                      )}
                      {request.rejectedDate && (
                        <span>Rejected: {formatDate(request.rejectedDate)}</span>
                      )}
                    </div>

                    {/* Cancel Button - Only show for Pending/Submitted/Draft requests */}
                    {canCancelRequest(request.leaveStatus || request.status) && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <button
                          onClick={() => handleCancelRequest(request)}
                          disabled={cancellingRequestId === (request.RequestId || request.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                          {cancellingRequestId === (request.RequestId || request.id)
                            ? 'Cancelling...'
                            : 'Cancel Leave Request'}
                        </button>
                      </div>
                    )}

                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
    </>
  );

  const confirmationModal = confirmModal.open && confirmModal.request && (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={() => setConfirmModal({ open: false, request: null })} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <Trash2 className="w-5 h-5 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Cancel Leave Request?</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">Are you sure you want to cancel this leave request? This action cannot be undone.</p>
        <div className="bg-gray-50 rounded-xl p-4 mb-6 space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Leave Type</span>
            <span className="font-medium text-gray-900">{confirmModal.request.leaveType} - {confirmModal.request.leaveTypeName || '—'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Dates</span>
            <span className="font-medium text-gray-900">{formatDate(confirmModal.request.startDate)} – {formatDate(confirmModal.request.endDate)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Duration</span>
            <span className="font-medium text-gray-900">
              {confirmModal.request.leaveAmountUnit === 'Hours'
                ? `${confirmModal.request.totalHours || 0} hour${confirmModal.request.totalHours !== 1 ? 's' : ''}`
                : `${confirmModal.request.totalHours || 0} day${confirmModal.request.totalHours !== 1 ? 's' : ''}`}
            </span>
          </div>
        </div>
        <div className="flex gap-3 justify-end">
          <button
            onClick={() => setConfirmModal({ open: false, request: null })}
            className="px-5 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Keep Request
          </button>
          <button
            onClick={handleConfirmCancel}
            className="px-5 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
          >
            Yes, Cancel
          </button>
        </div>
      </div>
    </div>
  );

  if (hideCard) {
    return (
      <>
        {content}
        {confirmationModal}
      </>
    );
  }

  return (
    <>
      <div className="bg-white rounded-xl shadow-lg p-6 lg:p-8">
        {content}
      </div>
      {confirmationModal}
    </>
  );
};

export default LeaveRequestStatus;

